// Shared venues domain client.
//
// Owns the `/api/venues/*` paths. The venue-request workflow
// (`/api/venue-requests/*`, `/api/guardian/venue-requests/*`) is a separate
// domain.
//
// Policy writes accept a VenuePolicy and send the canonical { policy } body.
// The server still accepts legacy unwrapped bodies for older installed clients.
//
// The adapter mirrors the createApiClient method surface:
//   get(path, options), post(path, body, options),
//   patch(path, body, options), put(path, body, options)

import { readVenuePolicyRequest } from '../venue-requests.js';
import { readVenueIdentifier, readVenueListQuery, readVenueResourceQuery, readVenueFindRequest } from '../venue-read-requests.js';
export { readVenueIdentifier, readVenueListQuery, readVenueResourceQuery, readVenueFindRequest } from '../venue-read-requests.js';
import { readVenueWriteRequest } from '../venue-write-requests.js';
export { VenueRequestContractError, readVenuePolicyRequest } from '../venue-requests.js';
export { VenueWriteContractError, readVenueWriteRequest } from '../venue-write-requests.js';

export const VENUE_PATHS = {
  collection: '/api/venues',
  find: '/api/venues/find',
  forUser: '/api/venues/for-user',
  residenceFromUserAddress: '/api/venues/residence/from-user-address',
  eligible: '/api/venues/eligible',
  preview: '/api/venues/preview',
};

export const buildVenuePath = (id) =>
  `/api/venues/${encodeURIComponent(id)}`;

export const buildVenuePolicyPath = (id) =>
  `/api/venues/${encodeURIComponent(id)}/policy`;

export const createVenuesClient = (adapter) => {
  if (!adapter || typeof adapter !== 'object') {
    throw new TypeError('createVenuesClient requires a transport adapter.');
  }

  return {
    list(options = {}) {
      readVenueListQuery(options.params || {});
      return adapter.get(VENUE_PATHS.collection, options);
    },

    find(payload, options = {}) {
      return adapter.post(VENUE_PATHS.find, readVenueFindRequest(payload), options);
    },

    create(venue, options = {}) {
      return adapter.post(VENUE_PATHS.collection, readVenueWriteRequest(venue, 'create'), options);
    },

    createForUser(payload, options = {}) {
      return adapter.post(VENUE_PATHS.forUser, readVenueWriteRequest(payload, 'forUser'), options);
    },

    createResidenceFromUserAddress(body, options = {}) {
      return adapter.post(VENUE_PATHS.residenceFromUserAddress, readVenueWriteRequest(body, 'fromAddress'), options);
    },

    update(id, updates, options = {}) {
      return adapter.patch(buildVenuePath(readVenueIdentifier(id)), readVenueWriteRequest(updates), options);
    },

    updatePolicy(id, policy, options = {}) {
      return adapter.put(buildVenuePolicyPath(readVenueIdentifier(id)), readVenuePolicyRequest(policy), options);
    },

    eligible(params = {}, options = {}) {
      return adapter.get(VENUE_PATHS.eligible, { ...options, params: readVenueResourceQuery(params) });
    },

    preview(params = {}, options = {}) {
      return adapter.get(VENUE_PATHS.preview, { ...options, params: readVenueResourceQuery(params, true) });
    },
  };
};

export { VenueResponseContractError, readVenueListResponse, readVenueDetailResponse, readVenuePolicyResponse, readVenuePreviewResponse, readVenueEligibilityResponse, readActivityVenueEligibilityResponse, readRelatedVenueResponse, toEligibleVenueOption, toRelatedVenueOption } from '../venue-responses.js';
